import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  CreditCard,
  Home,
  LayoutDashboard,
  LogOut,
  Settings,
} from "lucide-react";

import { useAuth } from "@/hooks/useAuth";
import { resolveDashboardPath } from "@/lib/roles";

const logo = "/vouchee-header-logo.png";

function titleize(segment) {
  if (!segment) return "";
  return segment
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getDashboardHome(user) {
  return resolveDashboardPath(user);
}

function getPageTitle(pathname) {
  const clean = (pathname || "/").split("?")[0];

  const known = new Map([
    ["/dashboard", "Dashboard"],
    ["/business-dashboard", "Business Dashboard"],
    ["/admin", "Admin Dashboard"],
    ["/admin/fraud-alerts", "Fraud Alerts"],
    ["/admin/settings/subscription-plans", "Subscription Plans"],
    ["/admin/settings/promo-codes", "Promo Codes"],
    ["/dashboard/invoicing", "Invoicing"],
    ["/dashboard/partnerships", "Partnerships"],
    ["/dashboard/analytics", "Analytics"],
    ["/dashboard/transactions", "Transactions"],
    ["/dashboard/services", "Service Catalog"],
    ["/dashboard/settings", "Settings"],
    ["/dashboard/commission-settings", "Commission Settings"],
    ["/dashboard/payouts", "Payouts"],
    ["/dashboard/rolodex", "Rolodex"],
    ["/dashboard/smart-lists", "Smart Lists"],
    ["/customers", "Customer Management"],
    ["/business-dashboard/import-customers", "Import Customers"],
    ["/business-contacts", "Customer Contacts"],
    ["/partnerships", "Partnerships"],
    ["/proposals", "Proposals"],
    ["/service-catalog", "Service Catalog"],
    ["/discount-codes", "Discount Codes"],
    ["/earnings", "Earnings"],
    ["/gift-cards", "Gift Cards"],
  ]);

  if (known.has(clean)) return known.get(clean);

  const segments = clean.split("/").filter(Boolean);
  if (segments.length === 0) return "Dashboard";

  const last = segments[segments.length - 1];
  const prev = segments[segments.length - 2];

  if (segments[0] === "dashboard" && segments.length === 1) return "Dashboard";
  if (segments[0] === "admin" && segments.length === 1)
    return "Admin Dashboard";

  const looksLikeId = /^[0-9a-f-]{8,}$/i.test(last);
  const label = looksLikeId ? prev : last;
  return titleize(label);
}

export default function DashboardHeader() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const dashboardHome = useMemo(() => getDashboardHome(user), [user]);
  const title = useMemo(
    () => getPageTitle(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [showDropdown]);

  return (
    <header className="sticky top-0 z-50 border-b bg-white">
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div ref={dropdownRef} className="relative shrink-0">
              <div className="flex items-center gap-1">
                <Link
                  to="/dashboard"
                  className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981]"
                  aria-label="Go to dashboard"
                >
                  <img
                    src={logo}
                    alt="Vouchee"
                    className="h-9 w-auto max-w-[120px] object-contain sm:h-10"
                  />
                </Link>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowDropdown((open) => !open);
                  }}
                  className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981]"
                  aria-label="Open account menu"
                  aria-expanded={showDropdown}
                  aria-haspopup="menu"
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      showDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {showDropdown && (
                <div
                  className="absolute left-0 top-full z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                  role="menu"
                >
                  <div className="border-b border-gray-100 px-4 py-2">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {user?.full_name || user?.name || user?.email || "Account"}
                    </p>
                    {user?.email ? (
                      <p className="truncate text-xs text-gray-500">{user.email}</p>
                    ) : null}
                  </div>
                  <Link
                    to="/dashboard"
                    onClick={() => setShowDropdown(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    role="menuitem"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setShowDropdown(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    role="menuitem"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>
                  <Link
                    to="/dashboard/payment-settings"
                    onClick={() => setShowDropdown(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    role="menuitem"
                  >
                    <CreditCard className="h-4 w-4" />
                    Payment Settings
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDropdown(false);
                      signOut();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    role="menuitem"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>

            <div className="min-w-0">
              <div className="text-sm text-gray-500">You are in</div>
              <div className="truncate text-base font-semibold text-gray-900 sm:text-lg">
                {title}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Link
              to={dashboardHome}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 sm:px-3"
              aria-label="Back to dashboard"
            >
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>

            <button
              type="button"
              onClick={signOut}
              className="flex items-center gap-2 rounded-lg px-3 py-2 font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
